<script lang="ts">
  import { onMount, onDestroy } from 'svelte'
  import { tStore } from '../../i18n'
  import {
    aiHistoryApi,
    type AiHistoryEntry,
    type AiHistorySortField,
    type AiHistoryStatus,
  } from '../../api/aiHistory'
  import { link } from 'svelte-spa-router'
  import PageHeader from '../../components/PageHeader.svelte'
  import StatusBadge from '../../components/StatusBadge.svelte'
  import ProcurementPager from './dashboard/ProcurementPager.svelte'
  import { aiScoreColor, formatDateTime } from '../../composables/format'
  import { useAbortable } from '../../composables/useAbortable'
  import { useDebouncedFn } from '../../composables/useDebouncedFn'

  const PAGE_SIZES = [10, 25, 50, 100, 200] as const
  const DEFAULT_PAGE_SIZE = 25

  // Mirrors the dashboard's silent background refresh: new AI results land
  // asynchronously (a bulk run or a single Run AI can finish at any time), so
  // the page quietly re-fetches the current page on an interval instead of
  // requiring a manual Refresh click to see them.
  const REFRESH_INTERVAL_MS = 5000

  const SEARCH_DEBOUNCE_MS = 300

  let entries = $state<AiHistoryEntry[]>([])
  let loading = $state(true)
  let errorMsg = $state<string | null>(null)

  let pageSize = $state(DEFAULT_PAGE_SIZE)
  let offset = $state(0)
  let totalCount = $state<number | null>(null)
  let hasMore = $state(false)
  let search = $state('')

  // Undefined field = backend default (newest result first). Clicking a
  // sortable header sets the field and toggles asc/desc, mirroring the
  // dashboard's procurement table.
  let sortField = $state<AiHistorySortField | undefined>(undefined)
  let sortDir = $state<'asc' | 'desc'>('desc')

  const { start } = useAbortable()
  let refreshTimer: ReturnType<typeof setInterval> | null = null
  let refreshController: AbortController | null = null
  let refreshInFlight = false

  onMount(() => {
    void load(start())
    refreshTimer = setInterval(() => {
      void refreshSilently()
    }, REFRESH_INTERVAL_MS)
  })

  onDestroy(() => {
    if (refreshTimer !== null) clearInterval(refreshTimer)
    refreshController?.abort()
    debouncedSearch.cancel()
  })

  // Opens a fresh request scope (cancelling any in-flight load) and reloads —
  // used by page-size changes, paging and search.
  function reload(): void {
    void load(start())
  }

  async function load(signal: AbortSignal) {
    loading = true
    errorMsg = null
    try {
      const filters = {
        limit: pageSize,
        offset,
        search: search || undefined,
        sort: sortField,
        order: sortField ? sortDir : undefined,
      }
      const [page, count] = await Promise.all([
        aiHistoryApi.list(filters, { signal }),
        aiHistoryApi.count({ search: filters.search }, { signal }),
      ])
      if (signal.aborted) return
      entries = page
      totalCount = count
      hasMore = offset + page.length < count
    } catch (err) {
      if (signal.aborted) return
      errorMsg = err instanceof Error ? err.message : String(err)
    } finally {
      if (!signal.aborted) loading = false
    }
  }

  // Same page/filters as `load`, but without flipping `loading` — avoids the
  // whole table flashing to a loading state every few seconds. Skipped while
  // a manual load (or a previous tick) is still in flight.
  async function refreshSilently() {
    if (loading || refreshInFlight) return
    refreshController?.abort()
    const controller = new AbortController()
    refreshController = controller
    refreshInFlight = true
    try {
      const filters = {
        limit: pageSize,
        offset,
        search: search || undefined,
        sort: sortField,
        order: sortField ? sortDir : undefined,
      }
      const [page, count] = await Promise.all([
        aiHistoryApi.list(filters, { signal: controller.signal }),
        aiHistoryApi.count({ search: filters.search }, { signal: controller.signal }),
      ])
      if (controller.signal.aborted) return
      entries = page
      totalCount = count
      hasMore = offset + page.length < count
    } catch {
      // Background refresh is best-effort; errors surface on the next
      // explicit reload (page-size change, paging, or a new search) instead.
    } finally {
      if (refreshController === controller) refreshController = null
      if (!controller.signal.aborted) refreshInFlight = false
    }
  }

  // The input updates `search` (and the field) on every keystroke so typing
  // feels immediate; only the actual request is debounced. Also resets to
  // page 1 — a search narrows the result set, so staying on e.g. page 3 of
  // the old, unfiltered list would likely just show an empty page.
  const debouncedSearch = useDebouncedFn(() => {
    offset = 0
    reload()
  }, SEARCH_DEBOUNCE_MS)

  function onSearchInput() {
    debouncedSearch()
  }

  function changePageSize(next: number) {
    if (next === pageSize) return
    pageSize = next
    offset = 0
    reload()
  }

  function prevPage() {
    if (offset === 0) return
    offset = Math.max(0, offset - pageSize)
    reload()
  }

  function nextPage() {
    if (!hasMore) return
    offset = offset + pageSize
    reload()
  }

  function goToPage(page: number) {
    offset = (page - 1) * pageSize
    reload()
  }

  // Toggle sorting for a column: first click on a new column sorts descending
  // (highest score / newest time first); clicking the active column flips the
  // direction. Paging resets to page 1 so the reordered list is read from the
  // top — mirrors the dashboard's procurement table.
  function toggleSort(field: AiHistorySortField) {
    if (sortField === field) {
      sortDir = sortDir === 'asc' ? 'desc' : 'asc'
    } else {
      sortField = field
      sortDir = 'desc'
    }
    offset = 0
    reload()
  }

  function sortIndicator(field: AiHistorySortField): string {
    if (sortField !== field) return '↕'
    return sortDir === 'asc' ? '▲' : '▼'
  }

  function ariaSort(field: AiHistorySortField): 'none' | 'ascending' | 'descending' {
    if (sortField !== field) return 'none'
    return sortDir === 'asc' ? 'ascending' : 'descending'
  }

  function sortLabel(field: AiHistorySortField, column: string): string {
    const state =
      sortField !== field
        ? $tStore('views.procurements.sort.none')
        : sortDir === 'asc'
          ? $tStore('views.procurements.sort.ascending')
          : $tStore('views.procurements.sort.descending')
    return `${column} — ${state}`
  }

  let currentPage = $derived(Math.floor(offset / pageSize) + 1)
  let totalPages = $derived(totalCount !== null ? Math.ceil(totalCount / pageSize) : null)
  let rangeFrom = $derived(entries.length === 0 ? 0 : offset + 1)
  let rangeTo = $derived(offset + entries.length)

  function tone(s: AiHistoryStatus) {
    if (s === 'completed') return 'success' as const
    if (s === 'failed') return 'danger' as const
    if (s === 'no_documents') return 'warning' as const
    return 'neutral' as const // cancelled
  }
</script>

<section class="history">
  <PageHeader
    title={$tStore('views.aiHistory.title')}
    subtitle={$tStore('views.aiHistory.subtitle')}
  >
    {#snippet actions()}
      <input
        class="input search-input"
        type="text"
        bind:value={search}
        oninput={onSearchInput}
        placeholder={$tStore('views.aiHistory.searchPlaceholder')}
        aria-label={$tStore('views.aiHistory.searchPlaceholder')}
      />
    {/snippet}
  </PageHeader>

  {#if loading}
    <p class="muted">{$tStore('views.aiHistory.loading')}</p>
  {:else if errorMsg}
    <p class="error">{errorMsg}</p>
  {:else if entries.length === 0}
    <p class="muted">
      {$tStore(search ? 'views.aiHistory.emptySearch' : 'views.aiHistory.empty')}
    </p>
  {:else}
    <div class="card table-wrap">
      <table class="table">
        <thead>
          <tr>
            <th>{$tStore('views.aiHistory.columns.title')}</th>
            <th>{$tStore('views.aiHistory.columns.buyer')}</th>
            <th aria-sort={ariaSort('aiScore')}>
              <button
                type="button"
                class="sort-btn"
                class:sort-btn--active={sortField === 'aiScore'}
                onclick={() => toggleSort('aiScore')}
                aria-label={sortLabel('aiScore', $tStore('views.aiHistory.columns.aiScore'))}
              >
                {$tStore('views.aiHistory.columns.aiScore')}
                <span class="sort-ind" aria-hidden="true">{sortIndicator('aiScore')}</span>
              </button>
            </th>
            <th>{$tStore('views.aiHistory.columns.status')}</th>
            <th aria-sort={ariaSort('time')}>
              <button
                type="button"
                class="sort-btn"
                class:sort-btn--active={sortField === 'time'}
                onclick={() => toggleSort('time')}
                aria-label={sortLabel('time', $tStore('views.aiHistory.columns.time'))}
              >
                {$tStore('views.aiHistory.columns.time')}
                <span class="sort-ind" aria-hidden="true">{sortIndicator('time')}</span>
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {#each entries as e (e.procurementId)}
            <tr>
              <td>
                <a href={`/procurements/${e.procurementId}`} use:link class="title-link">
                  {e.title}
                </a>
              </td>
              <td>{e.buyerName ?? '—'}</td>
              <td>
                {#if e.aiScore !== null}
                  <span style:color={aiScoreColor(e.aiScore)}>{e.aiScore}%</span>
                {:else}
                  —
                {/if}
              </td>
              <td>
                <StatusBadge
                  label={$tStore(`views.aiHistory.status.${e.status}`)}
                  tone={tone(e.status)}
                />
              </td>
              <td>{formatDateTime(e.lastResultAt)}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <ProcurementPager
      {pageSize}
      {offset}
      {rangeFrom}
      {rangeTo}
      {currentPage}
      {totalPages}
      {totalCount}
      {hasMore}
      {loading}
      pageSizes={PAGE_SIZES}
      onPageSizeChange={changePageSize}
      onPrev={prevPage}
      onNext={nextPage}
      onGoToPage={goToPage}
    />
  {/if}
</section>

<style lang="scss">
  @use '../../assets/styles/variables' as v;

  .history {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
  }

  .search-input {
    width: 16rem;
    max-width: 100%;
  }

  .table-wrap {
    overflow-x: auto;
    padding: 0;
  }

  .title-link {
    font-weight: 500;
  }

  // Sortable column header. Inherits the global `thead th` typography (colour,
  // weight, uppercase) so it's indistinguishable from a plain heading until
  // hovered — only the sort glyph hints at interactivity. Mirrors the
  // dashboard's procurement table.
  .sort-btn {
    display: inline-flex;
    align-items: center;
    gap: v.$space-xs;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    font: inherit;
    color: inherit;
    text-transform: inherit;
    letter-spacing: inherit;

    &:hover {
      color: v.$color-primary;
    }

    &--active {
      color: v.$color-primary;
    }
  }

  .sort-ind {
    font-size: 0.7rem;
    line-height: 1;
    opacity: 0.7;
  }

  .sort-btn--active .sort-ind {
    opacity: 1;
  }
</style>
